// Uploads a file to the server's private storage (R2) with progress, measuring images and videos first so
// messages can reserve their exact space before anything loads. Large photos are made smaller first (optimizeImage).
// Files over one part (UPLOAD_PART_BYTES) go up in parts (worker/src/uploads.ts), two at a time, each retried before
// giving up; cancelling aborts the upload on the server too. Nothing here reads a file into JS: parts are slices of
// the File, sent as they are by the browser, so a 100 MB upload costs the page no work in proportion to its size.
// The one exception is a file an app's frame handed over (`inMemory`, see uploadFile).
// Before anything leaves the browser, a photo's or video's location is removed and the copy is read back (stripLocation.ts,
// #352): the location never crosses the network.
import { UPLOAD_PART_BYTES, type Attachment, type ClientConfig, type FileVariantName } from "@bloom/shared";
import { BUILD } from "./buildInfo.ts";
import { keepExif } from "./keepExif.ts";
import { LocationRefused, makeSafe } from "./stripLocation.ts";

export type FileAttachment = Extract<Attachment, { kind: "file" }>;

export const fileUrl = (key: string) => `/api/files/${key.split("/").map(encodeURIComponent).join("/")}`;
export const isImage = (type: string) => /^image\/(png|jpeg|gif|webp|avif)$/.test(type);
export const isVideo = (type: string) => /^video\/(mp4|webm|quicktime)$/.test(type);

/**
 * Pictures bigger than this are never drawn from the original in the chat list: until a smaller copy exists (or when
 * none can be made), a placeholder with the name and size shows instead, loaded on a tap.
 */
export const INLINE_ORIGINAL_MAX = 10 * 1024 * 1024;
/** Pictures bigger than this aren't decoded in the browser (to measure, shrink or preview them): their size comes from the header. */
const DECODE_MAX = 30 * 1024 * 1024;

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** A picture's size from the first bytes of its file (PNG, GIF, WebP, JPEG with its EXIF orientation), without decoding it. */
export function sizeFromHeader(b: Uint8Array, type: string): { width?: number; height?: number } {
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const ok = (w: number, h: number) => (w > 0 && h > 0 ? { width: w, height: h } : {});
  try {
    if (type === "image/png" && dv.getUint32(12) === 0x49484452) return ok(dv.getUint32(16), dv.getUint32(20));
    if (type === "image/gif") return ok(dv.getUint16(6, true), dv.getUint16(8, true));
    if (type === "image/webp") {
      const kind = fourcc(b, 12);
      if (kind === "VP8X") return ok(1 + (dv.getUint32(24, true) & 0xffffff), 1 + (dv.getUint32(27, true) & 0xffffff));
      if (kind === "VP8 ") return ok(dv.getUint16(26, true) & 0x3fff, dv.getUint16(28, true) & 0x3fff);
      if (kind === "VP8L") {
        const v = dv.getUint32(21, true);
        return ok(1 + (v & 0x3fff), 1 + ((v >> 14) & 0x3fff));
      }
    }
    if (type === "image/jpeg") {
      let orientation = 1;
      for (let at = 2; at + 9 < b.length; ) {
        if (b[at] !== 0xff) return {};
        const marker = b[at + 1]!;
        const len = dv.getUint16(at + 2);
        if (marker === 0xe1 && fourcc(b, at + 4) === "Exif") {
          const t = at + 10;
          const le = b[t] === 0x49;
          const ifd = t + dv.getUint32(t + 4, le);
          for (let i = 0, n = dv.getUint16(ifd, le); i < n; i++) {
            if (dv.getUint16(ifd + 2 + i * 12, le) === 0x0112) orientation = dv.getUint16(ifd + 2 + i * 12 + 8, le);
          }
        }
        // Start of frame (not DHT, JPG or DAC): height, then width.
        if (marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)) {
          const h = dv.getUint16(at + 5);
          const w = dv.getUint16(at + 7);
          return orientation >= 5 && orientation <= 8 ? ok(h, w) : ok(w, h);
        }
        at += 2 + len;
      }
    }
  } catch {
    // too short or malformed
  }
  return {};
}

async function measure(file: File): Promise<{ width?: number; height?: number }> {
  try {
    if (isImage(file.type) && file.size > DECODE_MAX) return sizeFromHeader(new Uint8Array(await file.slice(0, 256 * 1024).arrayBuffer()), file.type);
    if (isImage(file.type)) {
      const bmp = await createImageBitmap(file);
      const size = { width: bmp.width, height: bmp.height };
      bmp.close();
      return size;
    }
    if (isVideo(file.type)) {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;
      await new Promise((res, rej) => {
        v.onloadedmetadata = res;
        v.onerror = rej;
      });
      URL.revokeObjectURL(url);
      return { width: v.videoWidth, height: v.videoHeight };
    }
  } catch {
    // unreadable: show it as a plain file
  }
  return {};
}

/** A display copy the server made of a file (shared/src/media.ts), if it has one by that name. */
export function variantUrl(a: FileAttachment, name: FileVariantName): string | undefined {
  return a.variants?.some((v) => v.name === name) ? `${fileUrl(a.key)}/${name}` : undefined;
}

// ---- Smaller photos before upload ----
// A phone photo is often 3–8 MB and 4000+ px. Pictures are redrawn at most MAX_EDGE px on the long side and saved as
// WebP (JPEG where the browser can't write WebP), which also drops hidden metadata such as GPS location. The
// browser applies EXIF orientation while decoding, so the result is upright. Animations (GIF, animated WebP, APNG)
// stay as they are, and so does anything the result isn't smaller than.

const MAX_EDGE = 2560;
const QUALITY = 0.85;
const REENCODE = /^image\/(jpeg|png|webp|heic|heif)$/;
/** Small pictures that already fit aren't worth a lossy pass. */
const SMALL_ENOUGH = 200 * 1024;

const fourcc = (b: Uint8Array, at: number) => String.fromCharCode(b[at]!, b[at + 1]!, b[at + 2]!, b[at + 3]!);

/** Animated WebP (VP8X animation flag) or APNG (an acTL chunk before the image data). */
async function isAnimated(file: File): Promise<boolean> {
  if (file.type !== "image/webp" && file.type !== "image/png") return false;
  const b = new Uint8Array(await file.slice(0, 256 * 1024).arrayBuffer());
  if (file.type === "image/webp") return fourcc(b, 12) === "VP8X" && (b[20]! & 0x02) !== 0;
  const dv = new DataView(b.buffer);
  for (let at = 8; at + 8 <= b.length; at += 12 + dv.getUint32(at)) {
    const t = fourcc(b, at + 4);
    if (t === "acTL") return true;
    if (t === "IDAT") return false;
  }
  return false;
}

function encode(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, QUALITY));
}

/** The picture to upload instead of `file`, with its size; or null to upload the file as it is. */
export async function optimizeImage(file: File): Promise<{ file: File; width: number; height: number } | null> {
  if (!REENCODE.test(file.type) || file.size > DECODE_MAX || (await isAnimated(file))) return null;
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return null; // e.g. HEIC outside Safari: the server converts it
  }
  try {
    const heic = /heic|heif/.test(file.type);
    const s = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
    if (s === 1 && file.size <= SMALL_ENOUGH && !heic) return null;
    const width = Math.max(1, Math.round(bmp.width * s));
    const height = Math.max(1, Math.round(bmp.height * s));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const g = canvas.getContext("2d");
    if (!g) return null;
    g.imageSmoothingQuality = "high";
    g.drawImage(bmp, 0, 0, width, height);
    let blob = await encode(canvas, "image/webp");
    // Safari can't write WebP (it hands back a PNG). JPEG has no transparency, so only photos fall back to it.
    if (blob?.type !== "image/webp") blob = file.type === "image/jpeg" || heic ? await encode(canvas, "image/jpeg") : null;
    canvas.width = canvas.height = 0; // free the pixels now (Safari keeps canvases around)
    if (blob) blob = await keepExif(file, blob, width, height); // the camera, date and settings (#541); no location
    if (!blob || (!heic && blob.size >= file.size)) return null;
    const ext = blob.type === "image/webp" ? "webp" : "jpg";
    const name = `${(file.name || "image").replace(/\.[^.]*$/, "") || "image"}.${ext}`;
    return { file: new File([blob], name, { type: blob.type, lastModified: file.lastModified }), width, height };
  } catch {
    return null;
  } finally {
    bmp.close();
  }
}

export interface UploadTask {
  done: Promise<FileAttachment>;
  cancel: () => void;
}

class UploadError extends Error {
  constructor(
    message: string,
    /** Worth trying again (the network, or the server having a bad moment); not when it refused the request. */
    readonly retry: boolean,
  ) {
    super(message);
  }
}
const CANCELLED = "Cancelled.";
/** Who is uploading, for the server's `upload_had_location` log: this client and its build. */
export const CLIENT = `web/${BUILD.commit.replace(/[^A-Za-z0-9._+-]/g, "").slice(0, 40) || "unknown"}`;

/** A request that moves no bytes for this long is dead (#514): an upload's body, or the server's answer after it. */
export const UPLOAD_IDLE_MS = 60_000;
/** The same for a small request with no body to move (starting or completing an upload). */
export const UPLOAD_CALL_MS = 15_000;

/** One request with upload progress. Resolves with the JSON answer. It fails after an idle spell (above), and the caller may try again. */
function send<T>(method: string, url: string, body: Blob | string | null, headers: Record<string, string>, onProgress: ((loaded: number) => void) | null, live: Set<XMLHttpRequest>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    live.add(xhr);
    let idle: ReturnType<typeof setTimeout> | undefined;
    let timedOut = false;
    const watch = () => {
      clearTimeout(idle);
      idle = setTimeout(() => {
        timedOut = true;
        xhr.abort();
      }, onProgress ? UPLOAD_IDLE_MS : UPLOAD_CALL_MS);
    };
    watch();
    xhr.open(method, url);
    xhr.setRequestHeader("x-bloom-client", CLIENT);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        watch();
        onProgress(e.loaded);
      };
    }
    xhr.onload = () => {
      clearTimeout(idle);
      live.delete(xhr);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          return resolve((xhr.responseText ? JSON.parse(xhr.responseText) : null) as T);
        } catch {
          return reject(new UploadError("Upload failed (bad answer).", true));
        }
      }
      let message = `Upload failed (${xhr.status}).`;
      try {
        message = (JSON.parse(xhr.responseText) as { message?: string }).message ?? message;
      } catch {
        // keep the generic message
      }
      reject(new UploadError(message, xhr.status >= 500 || xhr.status === 408 || xhr.status === 429));
    };
    xhr.onerror = () => {
      clearTimeout(idle);
      live.delete(xhr);
      reject(new UploadError("Upload failed. Check your connection.", true));
    };
    xhr.onabort = () => {
      clearTimeout(idle);
      live.delete(xhr);
      if (timedOut) return reject(new UploadError("Upload timed out. Check your connection.", true));
      reject(new UploadError(CANCELLED, false));
    };
    xhr.send(body);
  });
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
/** Parts in flight at once: enough to fill most connections, few enough to leave room for chat. */
const PARALLEL_PARTS = 2;
/** Tries per part before the upload gives up (with a pause between, longer each time). */
const PART_TRIES = 3;

/**
 * `inMemory`: send copies of the file's bytes, a part at a time, never the File itself. For a file picked in another
 * page (an app's frame, on its own site and so in its own process, through the SDK's `upload`): Chrome kills a page
 * that hands XMLHttpRequest a picked file another process was given (RESULT_CODE_KILLED_BAD_MESSAGE), since only that
 * process may upload it. Reading it is allowed, so each part is copied just before it goes (at most two parts held).
 */
export function uploadFile(serverId: string, original: File, onProgress: (fraction: number) => void, o: { inMemory?: boolean } = {}): UploadTask {
  const body = async (b: Blob) => (o.inMemory ? new Blob([await b.arrayBuffer()], { type: b.type }) : b);
  const live = new Set<XMLHttpRequest>();
  let cancelled = false;
  /** A big upload's id once started, so cancelling (or failing) can abort it on the server. */
  let started: string | null = null;
  const base = `/api/servers/${serverId}`;
  const abortServer = () => {
    if (!started) return;
    const id = started;
    started = null;
    void fetch(`${base}/uploads/${id}`, { method: "DELETE", keepalive: true }).catch(() => {});
  };
  const done = (async () => {
    const smaller = await optimizeImage(original);
    let safe;
    try {
      safe = await makeSafe(smaller?.file ?? original);
    } catch (e) {
      if (e instanceof LocationRefused) throw new UploadError(e.message, false);
      throw e;
    }
    const file = safe.file;
    const { width, height } = safe.outcome === "reencoded" ? await measure(file) : (smaller ?? (await measure(file)));
    if (cancelled) throw new Error(CANCELLED);
    if (file.size <= UPLOAD_PART_BYTES) {
      const headers: Record<string, string> = { "content-type": file.type || "application/octet-stream", "x-file-name": encodeURIComponent(file.name || "file") };
      if (width && height) Object.assign(headers, { "x-width": String(width), "x-height": String(height) });
      return send<FileAttachment>("PUT", `${base}/files`, await body(file), headers, (n) => onProgress(n / file.size), live);
    }
    // ---- In parts ----
    const json = { "content-type": "application/json" };
    const start = await send<{ id: string; partSize: number; parts: number }>(
      "POST",
      `${base}/uploads`,
      JSON.stringify({ name: file.name || "file", type: file.type || "application/octet-stream", size: file.size, width, height }),
      json,
      null,
      live,
    );
    started = start.id;
    if (cancelled) throw new Error(CANCELLED);
    const loaded = new Array<number>(start.parts).fill(0);
    const report = () => onProgress(Math.min(0.999, loaded.reduce((a, b) => a + b, 0) / file.size));
    const etags: { partNumber: number; etag: string }[] = [];
    let next = 1;
    const sendPart = async (n: number) => {
      const blob = await body(file.slice((n - 1) * start.partSize, Math.min(file.size, n * start.partSize)));
      for (let attempt = 1; ; attempt++) {
        try {
          return await send<{ partNumber: number; etag: string }>("PUT", `${base}/uploads/${start.id}/${n}`, blob, {}, (b) => ((loaded[n - 1] = b), report()), live);
        } catch (e) {
          loaded[n - 1] = 0;
          report();
          if (cancelled || !(e instanceof UploadError) || !e.retry || attempt >= PART_TRIES) throw e;
          await sleep(1000 * attempt);
          if (cancelled) throw new Error(CANCELLED);
        }
      }
    };
    const lane = async () => {
      while (next <= start.parts) {
        if (cancelled) throw new Error(CANCELLED);
        const n = next++;
        etags.push(await sendPart(n));
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(PARALLEL_PARTS, start.parts) }, lane));
      if (cancelled) throw new Error(CANCELLED);
      onProgress(1);
      const attachment = await send<FileAttachment>("POST", `${base}/uploads/${start.id}/complete`, JSON.stringify({ parts: etags }), json, null, live);
      started = null;
      return attachment;
    } catch (e) {
      // Stop the other parts, and let the server drop what arrived.
      cancelled = true;
      for (const x of [...live]) x.abort();
      abortServer();
      throw e;
    }
  })();
  return {
    done,
    cancel: () => {
      cancelled = true;
      for (const x of [...live]) x.abort();
      abortServer();
    },
  };
}

let config: Promise<ClientConfig> | null = null;
/**
 * /api/config, fetched once per page load when it works. A failure (no network, a non-OK answer, a body that is not
 * JSON) rejects with a message fit to show, and is forgotten, so the next call asks again (#280). Only a config that
 * loaded can mean "not set up": a missing `klipyKey` or `appFrameOrigin` in it.
 */
export function getConfig(): Promise<ClientConfig> {
  if (config) return config;
  const request: Promise<ClientConfig> = fetch("/api/config").then(
    async (r) => {
      if (!r.ok) throw new Error(`Bloom answered ${r.status}.`);
      try {
        return (await r.json()) as ClientConfig;
      } catch {
        throw new Error("Bloom's answer wasn't readable.");
      }
    },
    () => {
      throw new Error("Couldn't reach the server.");
    },
  );
  config = request;
  request.catch(() => {
    // Only this request's own failure: a newer request is not ours to forget.
    if (config === request) config = null;
  });
  return request;
}

/** Why /api/config didn't load, in words for a screen: "GIFs didn't load: <this>". */
export function configFailureText(error: unknown): string {
  return error instanceof Error && error.message.trim() ? error.message.trim() : "Couldn't reach the server.";
}
